import { useQuery } from "@tanstack/react-query";
import apiClient, { ApiResponse } from "./apiClient";

export interface TopCustomer {
  loyalty_id: string;
  customer_name: string;
  balance: number;
}

export interface TopCustomersParams {
  start_date?: string;
  end_date?: string;
  limit?: number;
}

const QUERY_KEY = ["dashboard", "top-customers"];

const fetchTopCustomers = async (params: TopCustomersParams): Promise<TopCustomer[]> => {
  const { data: response } = await apiClient.get<ApiResponse<{ customers: TopCustomer[] }>>(
    "/v1/admin/reports/customers/top",
    { params }
  );
  return response.data.customers;
};

export const useTopCustomers = (params: TopCustomersParams = {}) =>
  useQuery({
    queryKey: [...QUERY_KEY, params],
    queryFn: () => fetchTopCustomers(params),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

// ── Referrals ────────────────────────────────────────────────────────────────

export interface Referral {
  referral_id: number;
  status: string;
  created_at: string;
  rewarded_at: string;
  referrer_loyalty_id: string;
  referrer_name: string;
  referee_loyalty_id: string;
  referee_name: string;
  referee_points_awarded: number;
  referrer_points_awarded: number;
}

const REFERRALS_QUERY_KEY = ["dashboard", "referrals"];

const fetchReferrals = async (): Promise<Referral[]> => {
  const { data: response } = await apiClient.get<ApiResponse<Referral[]>>(
    "/v1/admin/reports/referrals"
  );
  return response.data;
};

export const useReferrals = () =>
  useQuery({
    queryKey: REFERRALS_QUERY_KEY,
    queryFn: fetchReferrals,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

// ── Points Issuance ───────────────────────────────────────────────────────────

export interface LocationPoints {
  location_id: string;
  location_name: string;
  points: number;
}

export interface PointsParams {
  start: string;
  end: string;
}

const POINTS_QUERY_KEY = ["dashboard", "points"];

const fetchPoints = async (params: PointsParams): Promise<LocationPoints[]> => {
  const { data: response } = await apiClient.get<ApiResponse<LocationPoints[]>>(
    "/v1/admin/reports/points",
    { params }
  );
  return response.data;
};

export const usePoints = (params: PointsParams) =>
  useQuery({
    queryKey: [...POINTS_QUERY_KEY, params],
    queryFn: () => fetchPoints(params),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
